import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ThumbsUp, ThumbsDown, Tag, X, Sparkle, User as UserIcon, Lightbulb, FlaskConical } from "lucide-react";
import type { BrandSignal, SignalConfidence, SignalStatus } from "@/data/mockBrandSignalsData";
import { ConfidenceBars, CONFIDENCE_LABELS } from "./ConfidenceBars";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";

const STATUS_BG: Record<SignalStatus, string> = {
  validated: "bg-[hsl(142_55%_90%)]",
  testing: "bg-[hsl(234_99%_93%)]",
  hypothesis: "bg-[hsl(185_55%_88%)]",
  refuted: "bg-destructive/15",
};

const STATUS_LABELS: Record<SignalStatus, string> = {
  validated: "Validated",
  testing: "Testing",
  hypothesis: "Hypothesis",
  refuted: "Refuted",
};

const CONFIDENCE_BG: Record<SignalConfidence, string> = {
  high: "bg-[hsl(142_55%_90%)]",
  medium: "bg-[hsl(35_90%_88%)]",
  low: "bg-muted",
};

interface BrandSignalDetailDrawerProps {
  signal: BrandSignal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateTags: (signalId: string, tags: string[]) => Promise<void>;
  onCreateTest?: (signalId: string, hypothesis: string) => void;
}

export function BrandSignalDetailDrawer({
  signal,
  open,
  onOpenChange,
  onUpdateTags,
  onCreateTest,
}: BrandSignalDetailDrawerProps) {
  const readOnly = useViewerReadOnly();
  const [tagInput, setTagInput] = useState("");

  const handleAddTag = () => {
    if (!signal) return;
    const tag = tagInput.trim();
    if (tag && !signal.tags.includes(tag)) {
      onUpdateTags(signal.id, [...signal.tags, tag]);
    }
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    if (!signal) return;
    onUpdateTags(signal.id, signal.tags.filter((t) => t !== tag));
  };

  if (!signal) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] overflow-y-auto bg-card p-0"
      >
        <div className="px-6 pt-6 pb-4">
          <SheetHeader>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {signal.source === "ai" ? (
                <div
                  className="h-5 w-5 rounded flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg, #342799, #A242A2, #FF534C)" }}
                >
                  <Sparkle className="h-3 w-3 text-white" />
                </div>
              ) : (
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-muted">
                  <UserIcon className="h-3 w-3 text-muted-foreground" />
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {signal.source === "ai" ? "Signal AI" : "User"}
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <Badge
                variant="secondary"
                className={`border-0 text-foreground text-xs capitalize ${STATUS_BG[signal.status]}`}
              >
                {STATUS_LABELS[signal.status]}
              </Badge>
              <Badge
                variant="secondary"
                className={`border-0 text-foreground text-xs ${CONFIDENCE_BG[signal.confidence]}`}
              >
                <ConfidenceBars confidence={signal.confidence} />
                <span className="ml-1.5">{CONFIDENCE_LABELS[signal.confidence]} confidence</span>
              </Badge>
            </div>
            <SheetTitle className="text-lg font-semibold leading-snug text-foreground pr-8">
              {signal.hypothesis}
            </SheetTitle>
            <p className="text-sm text-muted-foreground mt-1">{signal.category}</p>
          </SheetHeader>
        </div>

        <div className="px-6 py-4 space-y-6">
          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">Evidence</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border p-3 flex items-center gap-2">
                <ThumbsUp className="h-4 w-4 text-trend-positive shrink-0" />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Supporting</p>
                  <p className="text-lg font-semibold text-foreground tabular-nums">{signal.supportingTests}</p>
                </div>
              </div>
              <div className="rounded-md border border-border p-3 flex items-center gap-2">
                <ThumbsDown className="h-4 w-4 text-trend-negative shrink-0" />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Refuting</p>
                  <p className="text-lg font-semibold text-foreground tabular-nums">{signal.refutingTests}</p>
                </div>
              </div>
            </div>
          </section>

          {(signal.tags.length > 0 || !readOnly) && (
            <section>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <Tag className="h-3.5 w-3.5" /> Tags
              </label>
              {signal.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {signal.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-[10px] font-semibold text-foreground bg-muted border-border flex items-center gap-1"
                    >
                      {tag}
                      {!readOnly && (
                        <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
              {!readOnly && (
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a tag..."
                    className="h-8 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                  >
                    <Tag className="h-3 w-3" /> Add
                  </Button>
                </div>
              )}
            </section>
          )}

          <Separator />

          <div className="flex items-center justify-between gap-2 pb-2">
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">
                Added {format(parseISO(signal.dateAdded), "MMM d, yyyy")}
              </p>
              <p className="text-xs text-muted-foreground">
                Updated {format(parseISO(signal.lastUpdated), "MMM d, yyyy")}
              </p>
            </div>
            {onCreateTest ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCreateTest(signal.id, signal.hypothesis)}
              >
                <FlaskConical className="h-3.5 w-3.5" /> Create a test
              </Button>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lightbulb className="h-3.5 w-3.5" />
                <span>{signal.category}</span>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
